import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAllDocs,
  fetchDocMaps,
  fetchDocument,
  fetchProjects,
  setDocumentCategory,
  type Me,
  type ProjectDocGlobal,
} from '../api';
import { fetchMe } from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime } from '../components/ui';
import { Markdown } from '../components/markdown';

/**
 * Toda la documentación de la plataforma, en una pantalla.
 *
 * La vista por proyecto tenía un techo evidente: con 813 documentos repartidos entre ocho
 * repositorios, buscar algo sin recordar dónde estaba obligaba a abrir ocho pestañas. Un
 * buscador que no cruza los proyectos no es un buscador.
 *
 * Sigue siendo un ESPEJO: se lee aquí, se edita en el repositorio. Lo único que se escribe
 * es la categoría.
 */
export function DocsGlobalPage(): JSX.Element {
  const client = useQueryClient();
  const [proyecto, setProyecto] = useState('');
  const [categoria, setCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);

  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false });
  const proyectos = useQuery({ queryKey: ['projects'], queryFn: fetchProjects });
  const mapas = useQuery({ queryKey: ['doc-maps'], queryFn: fetchDocMaps });
  const lista = useQuery({
    queryKey: ['docs-global', proyecto, categoria, busqueda],
    queryFn: () => fetchAllDocs({ project: proyecto, category: categoria, q: busqueda }),
  });
  const doc = useQuery({
    queryKey: ['doc', abierto],
    queryFn: () => fetchDocument(abierto as string),
    enabled: abierto !== null,
  });

  const clasificar = useMutation({
    mutationFn: (input: { id: string; category: string | null }) =>
      setDocumentCategory(input.id, input.category),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['docs-global'] });
      void client.invalidateQueries({ queryKey: ['doc', abierto] });
    },
  });

  if (lista.isPending) return <Loading />;
  if (lista.isError) return <ErrorBox error={lista.error} />;

  const { documents, categories, total, truncated } = lista.data;
  const yo = me.data as Me | undefined;
  const puedeClasificar = yo?.role === 'admin' || yo?.role === 'member';
  const etiqueta = (v: string | null): string =>
    categories.find((c) => c.value === v)?.label ?? 'Sin clasificar';

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Documentación</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        Toda la documentación de los proyectos, espejada de sus repositorios.{' '}
        <strong>Se lee aquí y se edita allí</strong>: la fuente de verdad sigue siendo git.
      </p>

      {/* Los mapas van arriba del buscador. Con más de mil documentos, buscar solo sirve si
          ya sabes qué buscas; esto es para cuando no lo sabes. Los proyectos SIN mapa se
          enseñan igual y apagados: el hueco es el dato que hace falta ver. */}
      {mapas.data && (
        <section className="mb-5 rounded-lg border border-[var(--color-border-subtle)] px-3 py-2.5">
          <p className="text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
            Empieza por el mapa
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-[var(--color-ink-muted)]">
            Una página por sistema: qué hace, qué piezas tiene y qué afecta a qué, qué puede
            fallar y dónde mirar. Lo demás es detalle.
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {mapas.data.projects.map((p) =>
              p.documentId ? (
                <li key={p.slug}>
                  <button
                    type="button"
                    onClick={() => setAbierto(p.documentId as string)}
                    className="rounded border border-[var(--color-border-subtle)] px-2 py-1 text-[12px] hover:bg-[var(--color-surface-muted)]"
                  >
                    {p.name}
                  </button>
                </li>
              ) : (
                <li
                  key={p.slug}
                  title={`${p.name} no tiene docs/MAPA.md en su repositorio`}
                  className="rounded border border-dashed border-[var(--color-border-subtle)] px-2 py-1 text-[12px] text-[var(--color-ink-muted)] opacity-60"
                >
                  {p.name} · sin mapa
                </li>
              ),
            )}
          </ul>
        </section>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-[12px]">
          <span className="mb-1 block text-[var(--color-ink-muted)]">Proyecto</span>
          <select
            value={proyecto}
            onChange={(e) => setProyecto(e.target.value)}
            className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1 text-[13px]"
          >
            <option value="">Todos</option>
            {(proyectos.data ?? []).map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-[12px]">
          <span className="mb-1 block text-[var(--color-ink-muted)]">Categoría</span>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1 text-[13px]"
          >
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-[16rem] flex-1 text-[12px]">
          <span className="mb-1 block text-[var(--color-ink-muted)]">Buscar</span>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Título o ruta…"
            className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1 text-[13px]"
          />
        </label>
      </div>

      <p className="mb-3 text-[12px] text-[var(--color-ink-muted)]">
        {total} documento(s)
        {truncated && (
          // Se dice cuando la lista está recortada. Presentar un resultado parcial como si
          // fuera todo es la forma más silenciosa de mentir en una pantalla.
          <>
            {' '}
            · mostrando los primeros {documents.length}.{' '}
            <strong>Afina los filtros para verlos todos.</strong>
          </>
        )}
      </p>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <div>
          {documents.length === 0 ? (
            <Empty>Ningún documento coincide con esos filtros.</Empty>
          ) : (
            <ul className="max-h-[70vh] space-y-0.5 overflow-y-auto pr-1">
              {documents.map((d: ProjectDocGlobal) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setAbierto(d.id)}
                    className={`w-full rounded px-2 py-1.5 text-left text-[13px] ${
                      abierto === d.id
                        ? 'bg-[var(--color-surface-muted)] font-medium'
                        : 'hover:bg-[var(--color-surface-muted)]'
                    }`}
                  >
                    <span className="block truncate">{d.title}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[var(--color-ink-muted)]">
                      <span className="font-medium">{d.project_name}</span>
                      <Badge>{etiqueta(d.category)}</Badge>
                      <span className="truncate">{d.path}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="min-w-0">
          {abierto === null ? (
            <Empty>Elige un documento de la lista.</Empty>
          ) : doc.isPending ? (
            <Loading />
          ) : doc.isError ? (
            <ErrorBox error={doc.error} />
          ) : (
            <article className="min-w-0">
              <header className="mb-4 border-b border-[var(--color-border-subtle)] pb-3">
                <h2 className="text-[16px] font-semibold">{doc.data.title}</h2>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--color-ink-muted)]">
                  <span className="font-medium">{doc.data.project_name}</span>
                  <span className="font-mono">{doc.data.path}</span>
                  <span>{Math.round(doc.data.bytes / 1024)} kB</span>
                  <span>
                    espejado <RelativeTime iso={doc.data.synced_at} />
                  </span>
                  {doc.data.html_url && (
                    <a
                      href={doc.data.html_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      ver y editar en GitHub
                    </a>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {puedeClasificar ? (
                    <label className="flex items-center gap-2 text-[12px]">
                      <span className="text-[var(--color-ink-muted)]">Categoría</span>
                      <select
                        value={doc.data.category ?? ''}
                        disabled={clasificar.isPending}
                        onChange={(e) =>
                          clasificar.mutate({
                            id: doc.data.id,
                            category: e.target.value === '' ? null : e.target.value,
                          })
                        }
                        className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
                      >
                        <option value="">Sin clasificar</option>
                        {categories.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <Badge>{etiqueta(doc.data.category)}</Badge>
                  )}
                </div>
                {clasificar.isError && <ErrorBox error={clasificar.error} />}
              </header>
              <Markdown texto={doc.data.content} />
            </article>
          )}
        </div>
      </div>
    </>
  );
}
